import api, { getCsrfToken } from './api';

export interface User {
  id: number;
  username: string;
  display_name?: string;
  email: string;
  phone?: string;
  client_type?: string;
  contact_name?: string;
  channel?: string;
  source?: string;
  group_number?: string;
  avatar?: string;
  wechat_nickname?: string;
  wechat_avatar?: string;
  roles: Array<{
    id: number;
    name: string;
    display_name?: string;
  }>;
  permissions: Array<{
    id: number;
    name: string;
  }>;
}

export interface LoginRequest {
  // 支持邮箱或用户名登录：传入 login
  login: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  display_name?: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: {
    user: User;
    access_token: string;
    token_type: string;
  };
  errors?: any;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

export interface MeResponse {
  success: boolean;
  data: {
    user: User;
    permissions: string[];
    roles: string[];
  };
}

class AuthService {
  // Login
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    // 先获取CSRF令牌
    await getCsrfToken();
    const response = await api.post('/auth/login', credentials);
    return response.data;
  }

  // Register
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    // 先获取CSRF令牌
    await getCsrfToken();
    const response = await api.post('/auth/register', userData);
    return response.data;
  }

  // Get current user
  async me(): Promise<MeResponse> {
    const response = await api.get('/me');
    return response.data;
  }

  // Get profile
  async getProfile(): Promise<{ success: boolean; data: { user: User } }> {
    const response = await api.get('/auth/profile');
    return response.data;
  }

  // Update profile
  async updateProfile(userData: Partial<Pick<User, 'username' | 'display_name' | 'email'>>): Promise<AuthResponse> {
    const response = await api.put('/auth/profile', userData);
    return response.data;
  }

  // Change password
  async changePassword(passwordData: {
    current_password: string;
    new_password: string;
    new_password_confirmation: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/change-password', passwordData);
    return response.data;
  }

  async forgotPassword(payload: ForgotPasswordRequest): Promise<{ success: boolean; message?: string; errors?: any }> {
    const response = await api.post('/auth/password/forgot', payload);
    return response.data;
  }

  async resetPassword(payload: ResetPasswordRequest): Promise<{ success: boolean; message?: string; errors?: any }> {
    const response = await api.post('/auth/password/reset', payload);
    return response.data;
  }

  // Logout
  async logout(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/logout');
    return response.data;
  }

  // Revoke all tokens
  async revokeAll(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/revoke-all');
    return response.data;
  }

  // Token management
  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  removeToken(): void {
    localStorage.removeItem('token');
  }

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }
}

const authService = new AuthService();
export default authService;
